import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getMyVenueProfile, updateMyVenueProfile } from '../../lib/api';
import { WizardLayout, WizardQuestion } from '../../components/onboarding/WizardLayout';
import VenueInventoryEditor from '../../components/onboarding/VenueInventoryEditor';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { validatePhone } from '../../lib/utils';

const TOTAL_STEPS = 5; // 3 detail steps + publish screen + equipment inventory

export default function VenueOnboardingPage() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();

  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [profile, setProfile] = useState({
    venueName: '',
    contactPersonName: '',
    phone: '',
    city: '',
    country: 'NL',
    venueAddress: '',
    publicTransitAccess: 'good',
    shortBio: '',
  });

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProfile = async () => {
    try {
      const response = await getMyVenueProfile();
      const p = response.data;

      // Already published — skip the wizard entirely
      if (p.profileStatus === 'published') {
        navigate('/venues/me');
        return;
      }

      setProfile({
        venueName: p.venueName || '',
        contactPersonName: p.contactPersonName || '',
        phone: p.phone || '',
        city: p.city || '',
        country: p.country || 'NL',
        venueAddress: p.venueAddress || '',
        publicTransitAccess: p.publicTransitAccess || 'good',
        shortBio: p.shortBio || '',
      });

      // Resume at last completed step
      if (p.venueName && p.contactPersonName && p.phone) {
        if (p.city && p.venueAddress) {
          if (p.isComplete) {
            setCurrentStep(5); // publish (terminal)
          } else {
            setCurrentStep(3);
          }
        } else {
          setCurrentStep(2);
        }
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please sign in again.');
        navigate('/login');
        return;
      }
      console.error('Failed to load venue profile:', error);
    }
  };

  const updateField = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const getStepData = () => {
    switch (currentStep) {
      case 1:
        return {
          venueName: profile.venueName,
          contactPersonName: profile.contactPersonName,
          phone: profile.phone,
        };
      case 2:
        return {
          city: profile.city,
          country: profile.country,
          venueAddress: profile.venueAddress,
          publicTransitAccess: profile.publicTransitAccess,
        };
      case 3:
        return {
          shortBio: profile.shortBio,
        };
      default:
        return {};
    }
  };

  const canContinue = () => {
    switch (currentStep) {
      case 1:
        return profile.venueName && profile.contactPersonName && profile.phone && !validatePhone(profile.phone);
      case 2:
        return profile.city;
      case 3:
        return true; // bio is optional
      case 4:
        return true;
      default:
        return false;
    }
  };

  const handleContinue = async () => {
    if (!canContinue()) return;
    setSaving(true);
    try {
      await updateMyVenueProfile(getStepData());
      const nextStep = currentStep + 1;
      setCurrentStep(Math.min(nextStep, TOTAL_STEPS));
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please sign in again.');
        navigate('/login');
        return;
      }
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      await updateMyVenueProfile({ visibilityPublic: true });
      toast.success('Profile published!');
      // Sync user context so profileStatus reflects 'published' and the
      // needsOnboarding() redirect loop from App.js is broken.
      await checkAuth();
      navigate('/venues/me');
    } catch (error) {
      toast.error('Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  // Equipment inventory (step 4) — skippable, never gates publishing. Wider shell.
  if (currentStep === 4) {
    return (
      <WizardLayout
        currentStep={currentStep}
        totalSteps={TOTAL_STEPS - 1}
        onBack={handleBack}
        onContinue={() => setCurrentStep(5)}
        canContinue={true}
        continueLabel="Continue"
        contentClassName="max-w-4xl"
      >
        <WizardQuestion
          title="What gear do you have?"
          subtitle="Your equipment inventory powers the rider compatibility check for incoming artists"
        />
        <VenueInventoryEditor />
        <div className="mt-6 text-center">
          <button
            onClick={() => setCurrentStep(5)}
            className="text-sm text-white/40 hover:text-white/60 transition-colors inline-flex items-center justify-center gap-1"
            data-testid="inventory-skip-btn"
          >
            Skip for now <ArrowRight className="h-3 w-3" />
          </button>
          <p className="text-xs text-white/30 mt-2">
            Artists can't run a rider check against this room until you list some gear.
          </p>
        </div>
      </WizardLayout>
    );
  }

  // Publish screen (step 5, terminal — go live, then to the venue dashboard)
  if (currentStep === 5) {
    return (
      <WizardLayout
        currentStep={currentStep}
        totalSteps={TOTAL_STEPS - 1}
        onBack={handleBack}
        onContinue={handlePublish}
        canContinue={true}
        isLoading={publishing}
        continueLabel="Publish Profile"
      >
        <div className="text-center py-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[color-mix(in_srgb,var(--s8-amber)_20%,transparent)] mb-6">
            <CheckCircle className="h-8 w-8 text-[var(--s8-amber)]" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">Ready to Publish?</h2>
          <p className="text-white/60 max-w-sm mx-auto mb-8">
            Your venue profile is set up. Once published, talent can find you and you can start creating requests.
          </p>

          <div className="bg-[#1a1a1a] border border-white/[0.08] rounded-xl p-5 text-left max-w-sm mx-auto space-y-3">
            <div className="flex justify-between">
              <span className="text-white/40 text-sm">Venue</span>
              <span className="text-white text-sm font-medium">{profile.venueName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-white/40 text-sm">Contact</span>
              <span className="text-white text-sm font-medium">{profile.contactPersonName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-white/40 text-sm">City</span>
              <span className="text-white text-sm font-medium">{profile.city}</span>
            </div>
          </div>

          <div className="mt-8 flex flex-col gap-3">
            <button
              onClick={() => navigate('/venues/me')}
              className="text-sm text-white/40 hover:text-white/60 transition-colors flex items-center justify-center gap-1"
            >
              Add more details later <ArrowRight className="h-3 w-3" />
            </button>
          </div>
        </div>
      </WizardLayout>
    );
  }

  return (
    <WizardLayout
      currentStep={currentStep}
      totalSteps={TOTAL_STEPS - 1}
      onBack={handleBack}
      onContinue={handleContinue}
      canContinue={canContinue()}
      isLoading={saving}
    >
      {/* Step 1: Identity */}
      {currentStep === 1 && (
        <>
          <WizardQuestion
            title="Tell us about your venue"
            subtitle="Basic contact details for talent to reach you"
          />
          <div className="space-y-5">
            <div className="space-y-2">
              <Label className="text-white/70 text-sm">Venue Name *</Label>
              <Input
                value={profile.venueName}
                onChange={(e) => updateField('venueName', e.target.value)}
                placeholder="e.g., Paradiso Amsterdam"
                className="h-12 bg-[#151515] border-white/[0.08] text-white"
                data-testid="venue-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70 text-sm">Contact Person *</Label>
              <Input
                value={profile.contactPersonName}
                onChange={(e) => updateField('contactPersonName', e.target.value)}
                placeholder="Primary contact name"
                className="h-12 bg-[#151515] border-white/[0.08] text-white"
                data-testid="contact-person-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70 text-sm">Phone (WhatsApp) *</Label>
              <Input
                value={profile.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                placeholder="+31612345678"
                className="h-12 bg-[#151515] border-white/[0.08] text-white"
                data-testid="phone-input"
              />
              <p className="text-xs text-white/30">Include country code</p>
              {profile.phone && validatePhone(profile.phone) && (
                <p className="text-xs text-red-400">{validatePhone(profile.phone)}</p>
              )}
            </div>
          </div>
        </>
      )}

      {/* Step 2: Location */}
      {currentStep === 2 && (
        <>
          <WizardQuestion
            title="Where are you located?"
            subtitle="Helps talent calculate travel and plan their journey"
          />
          <div className="space-y-5">
            <div className="space-y-2">
              <Label className="text-white/70 text-sm">City *</Label>
              <Input
                value={profile.city}
                onChange={(e) => updateField('city', e.target.value)}
                placeholder="Amsterdam"
                className="h-12 bg-[#151515] border-white/[0.08] text-white"
                data-testid="city-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70 text-sm">Country</Label>
              <Select value={profile.country} onValueChange={(val) => updateField('country', val)}>
                <SelectTrigger className="h-12 bg-[#151515] border-white/[0.08] text-white" data-testid="country-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NL">Netherlands</SelectItem>
                  <SelectItem value="BE">Belgium</SelectItem>
                  <SelectItem value="DE">Germany</SelectItem>
                  <SelectItem value="IE">Ireland</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-white/70 text-sm">Full Address</Label>
              <Input
                value={profile.venueAddress}
                onChange={(e) => updateField('venueAddress', e.target.value)}
                placeholder="e.g., Leidseplein 12, 1017 PT Amsterdam"
                className="h-12 bg-[#151515] border-white/[0.08] text-white"
                data-testid="address-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70 text-sm">Public Transit Access</Label>
              <Select value={profile.publicTransitAccess} onValueChange={(val) => updateField('publicTransitAccess', val)}>
                <SelectTrigger className="h-12 bg-[#151515] border-white/[0.08] text-white" data-testid="transit-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="good">Good (near metro/tram/bus)</SelectItem>
                  <SelectItem value="limited">Limited (some transit options)</SelectItem>
                  <SelectItem value="poor">Poor (car recommended)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </>
      )}

      {/* Step 3: About */}
      {currentStep === 3 && (
        <>
          <WizardQuestion
            title="Anything else talent should know?"
            subtitle="A short bio helps talent understand your vibe"
          />
          <div className="space-y-2">
            <Textarea
              value={profile.shortBio}
              onChange={(e) => updateField('shortBio', e.target.value)}
              placeholder="Tell talent about your venue — capacity, vibe, what makes it special..."
              maxLength={500}
              rows={5}
              className="bg-[#151515] border-white/[0.08] text-white min-h-[140px]"
              data-testid="bio-input"
            />
            <p className="text-xs text-white/30 text-right">{profile.shortBio.length}/500</p>
          </div>
        </>
      )}
    </WizardLayout>
  );
}
